import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule, Validators } from '@angular/forms';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { InputFieldComponent } from '../input-field/input-field.component';
import { DateInputFieldComponent } from '../date-input-field/date-input-field.component';
import { DatetimeInputFieldComponent } from '../datetime-input-field/datetime-input-field.component';
import { SelectInputFieldComponent } from '../select-input-field/select-input-field.component';
import { AddButtonComponent } from '../add-button/add-button.component';
import { MultiSelectComponent } from '../multi-select/multi-select.component';
import { AutocompleteComponent } from '../autocomplete/autocomplete.component';
import { FloatLabelVariant } from '../ui-types';
import { MapOptions, MapPoint } from '../map/map.types';
import { MapComponent } from '../map/map.component';
import { UiTabPanelDirective, UiTabsComponent } from '../tabs/tabs.component';

@Component({
  selector: 'ui-showcase',
  standalone: true,
  imports: [
    FormsModule,
    ToggleSwitchModule,
    InputFieldComponent,
    DateInputFieldComponent,
    DatetimeInputFieldComponent,
    SelectInputFieldComponent,
    MultiSelectComponent,
    AddButtonComponent,
    AutocompleteComponent,
    MapComponent,
    UiTabsComponent,
    UiTabPanelDirective,
  ],
  templateUrl: './ui-showcase.component.html',
  styleUrls: ['./ui-showcase.component.scss'],
})
export class UiShowcaseComponent implements OnInit {
  isDisabled = false;
  activeTab: number = 0;

  autocompleteControl = new FormControl('');
  textControl = new FormControl('');
  textWithErrorControl = new FormControl('', Validators.required);
  numberControl = new FormControl<number | null>(null);
  dateControl = new FormControl<Date | null>(null);
  datetimeControl = new FormControl<Date | null>(null);
  selectControl = new FormControl('');
  multiSelectControl = new FormControl<string[]>([]);
  variantControl = new FormControl('');
  variant: FloatLabelVariant = 'in';

  private destroyRef = inject(DestroyRef);

  selectOptions = [
    { id: '1', displayValue: 'Option 1' },
    { id: '2', displayValue: 'Option 2' },
    { id: '3', displayValue: 'Option 3' },
  ];

  selectVariantOptions = [
    { id: '1', displayValue: 'In (default)' },
    { id: '2', displayValue: 'On' },
    { id: '3', displayValue: 'Over' },
  ];

  constructor() {
    this.textWithErrorControl.markAsTouched();

    this.dateControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      // When the date changes, we want to update the datetime control to have the same date but keep the time from the datetime control
      if (value && this.datetimeControl.value) {
        value.setHours(this.datetimeControl.value.getHours());
        value.setMinutes(this.datetimeControl.value.getMinutes());
      }
      this.datetimeControl.setValue(value, { emitEvent: false });
    });

    this.datetimeControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      this.dateControl.setValue(value, { emitEvent: false });
    });
  }

  ngOnInit(): void {
    this.variantControl.setValue(this.getIdFromVariant(this.variant));
  }

  onDisabledToggleChange(disabled: boolean): void {
    this.isDisabled = disabled;

    const controls = [
      this.textControl,
      this.textWithErrorControl,
      this.numberControl,
      this.dateControl,
      this.datetimeControl,
      this.selectControl,
      this.multiSelectControl,
      this.autocompleteControl,
    ];

    controls.forEach((control) => {
      if (disabled) {
        control.disable({ emitEvent: false });
      } else {
        control.enable({ emitEvent: false });
      }
    });
  }

  onVariantChange(value: string): void {
    const newVariant = this.selectVariantOptions.find((option) => option.id === value)?.displayValue;
    if (newVariant) {
      this.variant = this.getVariantFromId(value);
    }
  }

  onAddButtonClick(): void {}

  mapOptions: MapOptions = {
    // Replace this with your actual map options
    // styleUrl: 'https://tiles.openfreemap.org/styles/bright',
    // tileUrl: 'https://tiles.openfreemap.org/natural_earth/ne2sr/{z}/{x}/{y}.png',
    // tileUrl: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
    // attribution: '&copy; OpenStreetMap contributors',
    initialCenter: [0, 0] as const,
    initialZoom: 0,
    fitBoundsPadding: 32,
    clusterRadius: 50,
    clusterMaxZoom: 14,
    enableClick: true,
    enableHover: true,
    showNavigation: true,
    attribution: 'Completion',
  };

  readonly mapPoints: readonly MapPoint[] = [
    {
      id: '1',
      latitude: 59.904662600112815,
      longitude: 10.626999125227728,
      title: 'Oslo',
      details: ['Aker Solutions, Oslo'],
    },
    {
      id: '2',
      latitude: 63.43392343343267,
      longitude: 10.47116789837099,
      title: 'Trondheim',
      details: ['Aker Solutions, Trondheim'],
    },
    {
      id: '2a',
      latitude: 63.43392343343267,
      longitude: 10.47116789837099,
      title: 'Trondheim',
      details: ['Aker Solutions, Trondheim'],
    },
    {
      id: '2b',
      latitude: 63.43392343343267,
      longitude: 10.47116789837099,
      title: 'Trondheim',
      details: ['Aker Solutions, Trondheim'],
    },
    {
      id: '3',
      latitude: 60.29291892496909,
      longitude: 5.279397940582086,
      title: 'Bergen',
      details: ['Aker Solutions, Bergen'],
    },
    {
      id: '5',
      latitude: 59.76680244536363,
      longitude: 5.493857540562593,
      title: 'Leirvik',
      details: ['Aker Solutions, Leirvik'],
    },
    {
      id: '6',
      latitude: 63.78910622809546,
      longitude: 11.448711427219633,
      title: 'Verdal',
      details: ['Aker Solutions, Verdal'],
    },
    {
      id: '6a',
      latitude: 63.78910622809546,
      longitude: 11.448711427219633,
      title: 'Verdal',
      details: ['Aker Solutions, Verdal'],
    },
    {
      id: '7',
      latitude: 58.42510611168018,
      longitude: 5.9816587405134625,
      title: 'Egersund',
      details: ['Aker Solutions, Egersund'],
    },
  ];

  private getIdFromVariant(variant: FloatLabelVariant): string {
    switch (variant) {
      case 'in':
        return '1';
      case 'on':
        return '2';
      case 'over':
        return '3';
      default:
        return '1';
    }
  }

  private getVariantFromId(id: string): FloatLabelVariant {
    switch (id) {
      case '1':
        return 'in';
      case '2':
        return 'on';
      case '3':
        return 'over';
      default:
        return 'in';
    }
  }
}
